import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, MailIcon } from "lucide-react";
import { scrollToElement } from "../../utils/scroll";
import { trackOnce } from "../../utils/tracking";
import { toE164 } from "../../utils/phone";
import { CALENDAR_EMBED_SCRIPT, CALENDAR_URL, type Contact } from "./useApplicationWizard";
type WizardResultProps = {
  qualified: boolean;
  contact: Contact;
};
const BOOKING_COMPLETE_MESSAGE = "msgsndr-booking-complete";
export function WizardResult({
  qualified,
  contact
}: WizardResultProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!rootRef.current) return;
    // Account for the sticky wizard header on mobile.
    const extra = window.matchMedia("(max-width: 767px)").matches ? 56 : 0;
    scrollToElement(rootRef.current, extra);
  }, []);
  return <div ref={rootRef} role="status" data-id="element-73" data-mp="3qgmg2jprbw9p">
      {qualified ? <QualifiedScreen contact={contact} data-id="element-74" data-mp="3lht26q6k6390" /> : <NotQualifiedScreen data-id="element-75" data-mp="3liuawo03qdnk" />}
    </div>;
}
function QualifiedScreen({
  contact
}: {
  contact: Contact;
}) {
  const [loaded, setLoaded] = useState(false);
  const bookingUrl = useMemo(() => buildBookingUrl(contact), [contact]);

  // Booking widget reports a confirmed booking via postMessage.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (Array.isArray(event.data) && event.data[0] === BOOKING_COMPLETE_MESSAGE) {
        trackOnce("Schedule");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  useEffect(() => {
    trackOnce("ViewContent");
    if (document.querySelector(`script[src="${CALENDAR_EMBED_SCRIPT}"]`)) return;
    const script = document.createElement("script");
    script.src = CALENDAR_EMBED_SCRIPT;
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
  }, []);
  return <div data-id="element-76" data-mp="23f2gip6r791s">
      <span className="flex h-10 w-10 items-center justify-center rounded-[3px] border border-gold" data-id="element-77" data-mp="23fuci149j8sl">
        <CheckIcon className="h-5 w-5 text-gold" strokeWidth={2.5} aria-hidden="true" data-id="element-78" data-mp="2d9gh9ri046h7" />
      </span>
      <h3 className="mt-8 font-display text-[28px] uppercase leading-[1.05] tracking-[-0.01em] text-cream sm:text-[36px]" data-id="element-79" data-mp="28czub0mjfy5p">
        <span className="text-gold" data-id="element-80" data-mp="23asdnxqrjtx1">¡Calificas!</span> Elige el horario de tu diagnóstico (15 min)
      </h3>

      <div className="relative mt-8 min-h-[720px] md:min-h-[640px]" data-id="element-81" data-mp="1zd2ymf1qdz15">
        {!loaded && <div className="absolute inset-0 overflow-hidden rounded-[3px] border border-line bg-surface" aria-hidden="true" data-id="element-82" data-mp="1ue043ojdnb7f">
            <div className="absolute inset-x-0 top-0 h-px animate-pulse bg-gold/60" data-id="element-83" data-mp="1kn5i8la1mdmi" />
            <div className="space-y-4 p-6" data-id="element-84" data-mp="1fnsjp39t7i43">
              <div className="h-4 w-1/3 animate-pulse rounded-[2px] bg-white/[0.06]" data-id="element-85" data-mp="1fou78780hx2p" />
              <div className="h-px w-full bg-line" data-id="element-86" data-mp="1bkw9o0yc4ubr" />
              <div className="grid grid-cols-7 gap-2" data-id="element-87" data-mp="16mkv5cx3hvn7">
                {Array.from({
              length: 28
            }).map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-[2px] bg-white/[0.04]" data-id="element-88" data-mp="3iahdx6u0gho6" data-mpi={i} />)}
              </div>
            </div>
          </div>}
        <iframe src={bookingUrl} title="Agenda tu diagnóstico — Studio Web VIP" allow="payment" scrolling="no" onLoad={() => setLoaded(true)} className={`relative min-h-[720px] md:min-h-[640px] ${loaded ? "opacity-100" : "opacity-0"}`} style={{
        width: "100%",
        border: "none",
        overflow: "hidden"
      }} data-id="element-89" data-mp="3ramdiusyqbrs" />
      </div>

      <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex min-h-[48px] items-center text-[14px] text-muted underline-offset-4 transition-colors duration-200 ease-out hover:text-cream hover:underline" data-id="element-90" data-mp="38k4x9ppbh8nb">
        ¿No carga el calendario? Ábrelo aquí →
      </a>
    </div>;
}
function NotQualifiedScreen() {
  return <div data-id="element-91" data-mp="rbl5fm8wwwjm">
      <span className="flex h-10 w-10 items-center justify-center rounded-[3px] border border-steel" data-id="element-92" data-mp="rbblogegnlmd">
        <MailIcon className="h-5 w-5 text-gold" aria-hidden="true" data-id="element-93" data-mp="md0e6cbn1n7b" />
      </span>
      <h3 className="mt-8 font-display text-[26px] uppercase leading-[1.05] tracking-[-0.01em] text-cream sm:text-[30px]" data-id="element-94" data-mp="103vg14npz41e">
        Gracias por tu interés.
      </h3>
      <p className="mt-4 max-w-lg text-muted" data-id="element-95" data-mp="vbvjznarsva3">
        Por ahora este programa está diseñado para negocios con más volumen. Te
        enviaremos recursos útiles a tu email.
      </p>
    </div>;
}
function buildBookingUrl(contact: Contact): string {
  const [firstName = "", ...rest] = contact.name.trim().split(/\s+/);
  const params = new URLSearchParams({
    first_name: firstName,
    last_name: rest.join(" "),
    email: contact.email.trim(),
    phone: toE164(contact.phone)
  });
  return `${CALENDAR_URL}?${params.toString()}`;
}